import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EkonomiskAnalysDetailQuery from '../../Queries/EkonomiskAnalysDetailQuery/EkonomiskAnalysDetailQuery';
import EkonomiskAnalysDetailTable from '../EkonomiskAnalysDetailTable/EkonomiskAnalysDetailTable';
import EDTableRow from '../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import { ISumData } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import VerifikationDialog, {
  Tab,
} from '../VerifikationModal/VerifikationDialog';
import { EkonomiskAnalysContextConsumer } from '../Context/EkonomiskAnalysContext';
import {
  iSumDataToInputSum,
  colToInputCol,
  datesToTimeInterval,
} from '../../Utils/convertUtils';
import { tryConvertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import { ekanalys_ekanalys_columns } from '../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { rutinInfos } from '../../../../../_Common/Const/RutinInfo';
import EkonomiskAnalysBehorigTillRutinerQuery from '../../Queries/EkonomiskAnalysBehorigTillRutinerQuery/EkonomiskAnalysBehorigTillRutinerQuery';
import { ActiveDatabaseConsumer } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import EkonomiskAnalysSaveDBSettingMutation from '../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import { TEDUrvalType } from '../../../../../../../Types/UrvalTypes';
import classNames from 'classnames';
import {
  BudgetSummationModeType,
  SummationModeType,
} from '../../Utils/SummationModeUtils';

interface IProps {
  show: boolean;
  budgetDetailSummationMode: BudgetSummationModeType;
  codePrivilegesId: string;
  columnData?: ekanalys_ekanalys_columns;
  sums: ISumData[];
  rowLevel: number;
  /** Number of columns in parent table */
  numberOfColumns: number;
  sumValues: string[];
  dates: IEkonomiskAnalysFilledDates;
  endastBokfort: boolean;
  forceHelarsjamforelse: boolean | null;
  forstaBokfar: number;
  summationMode: SummationModeType;
  grundUrvalId: string | undefined;
  lopsaldoDetaljrad: boolean;
  ingaendeVardeLopSaldo: number | null;
  detailedSelection?: TEDUrvalType;
  visaSummeringar?: boolean;
  classes?: any;
}

interface IState {
  verifikationDialogOpen: boolean;
  /** Bokfdatum of the verifikation thats shown in verifikationDialog */
  openVerifikationBokfdatum: string | undefined;
  /** ID of the verifikation thats shown in verifikationDialog */
  openVerifikationId: string | undefined;
  /** The tab that verifikationDialog should be opened with */
  openVerifikationTab: Tab | undefined;
  /** Vernr och the open verifikation */
  openVerifikationNr: string | undefined;
}

const styles = (theme: Theme) => ({
  tableRow: {
    height: 0,
  },
  tableCell: {
    paddingTop: 0,
    paddingBottom: 0,
    border: 0,
    overflowX: 'auto',
    '&:last-child:only-child': {
      paddingRight: 0,
    },
  },
  root: {
    padding: 0,
    borderBottom: `3px solid ${theme.customProperties.EkonomiskAnalysDetailTableColors.border}`,
  },
  warningMessage: {
    paddingTop: '6px',
    paddingBottom: '6px',
  },
});

/**
 * Detaljtabell som visas när en specifik cell i en löpande analys eller en rad i en radbaserad analys klickas på, om detaljer finns.
 */
export class EkonomiskAnalysDetail extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      verifikationDialogOpen: false,
      openVerifikationBokfdatum: undefined,
      openVerifikationId: undefined,
      openVerifikationTab: undefined,
      openVerifikationNr: undefined,
    };
  }

  openVerifikationDialog = (
    id: string | undefined,
    verifikationNr: string,
    bokfdatum: string | undefined,
    tab?: Tab
  ) => {
    this.setState({
      verifikationDialogOpen: true,
      openVerifikationBokfdatum: bokfdatum,
      openVerifikationId: id,
      openVerifikationTab: tab,
      openVerifikationNr: verifikationNr,
    });
  };

  onVerifikationDialogClose = () => {
    this.setState({ verifikationDialogOpen: false });
  };

  onVerifikationDialogExited = () => {
    this.setState({
      openVerifikationBokfdatum: undefined,
      openVerifikationId: undefined,
      openVerifikationTab: undefined,
      openVerifikationNr: undefined,
    });
  };

  render() {
    return (
      <EkonomiskAnalysContextConsumer>
        {({ urval, userInfo }) => (
          <ActiveDatabaseConsumer>
            {({ activeDatabase }) => (
              <EkonomiskAnalysBehorigTillRutinerQuery
                dbname={activeDatabase ?? ''}
              >
                {({ availableRoutines }) => {
                  const canViewFullVerifikationsRader = availableRoutines.some(
                    a => a === rutinInfos.rtnWebBokfJournal.rutinID
                  );

                  return (
                    <EkonomiskAnalysSaveDBSettingMutation>
                      {saveDBSetting => {
                        return (
                          <EDTableRow
                            classes={{ root: this.props.classes.tableRow }}
                          >
                            <EDTableCell
                              colSpan={this.props.numberOfColumns + 1} // + 1 för att det ligger en osynlig kolumn i sista kolumnen i tabellen som ska fylla ut oanvänt utrymme
                              className={classNames(
                                this.props.classes.tableCell
                              )}
                              classes={
                                this.props.show
                                  ? { root: this.props.classes.root }
                                  : undefined
                              }
                            >
                              {this.props.columnData && this.props.show && (
                                <EkonomiskAnalysDetailQuery
                                  key={JSON.stringify(this.props.columnData)}
                                  column={this.props.columnData}
                                  variables={{
                                    detaljanalyslopande: {
                                      columns: [
                                        colToInputCol(this.props.columnData),
                                      ],
                                      sums: this.props.sums
                                        .slice(0, this.props.rowLevel + 1)
                                        .map(iSumDataToInputSum),
                                      sumvalues: this.props.sumValues,
                                      endast_bokfort: this.props.endastBokfort,
                                      force_helarsjamforelse:
                                        this.props.forceHelarsjamforelse,
                                    },
                                    kodbeh_id: this.props.codePrivilegesId,
                                    anvurval:
                                      tryConvertUIUrvalToTEDUrval(urval),
                                    tidsintervall: datesToTimeInterval(
                                      this.props.dates
                                    ),
                                    grundurval_id: this.props.grundUrvalId,
                                    detaljurval_selection:
                                      this.props.detailedSelection,
                                    forsta_bokfar:
                                      this.props.forstaBokfar.toString(),
                                  }}
                                  lopsaldoDetaljrad={
                                    this.props.lopsaldoDetaljrad
                                  }
                                >
                                  {({ analysDetail, isBudgetColumn }) => {
                                    return (
                                      <>
                                        <EkonomiskAnalysDetailTable
                                          columnData={analysDetail}
                                          budgetDetailSummationMode={
                                            this.props.budgetDetailSummationMode
                                          }
                                          detailSummationMode={
                                            this.props.summationMode
                                          }
                                          isBudgetColumn={isBudgetColumn}
                                          openVerifikationDialog={
                                            this.openVerifikationDialog
                                          }
                                          canViewFullVerifikationRader={
                                            canViewFullVerifikationsRader
                                          }
                                          lopsaldoDetaljrad={
                                            this.props.lopsaldoDetaljrad
                                          }
                                          ingaendeVardeLopSaldo={
                                            this.props.ingaendeVardeLopSaldo
                                          }
                                          saveDBSetting={saveDBSetting}
                                          defaultRowsPerPage={
                                            userInfo?.detailTableRowsPerPage
                                          }
                                          visaSummeringar={
                                            this.props.visaSummeringar
                                          }
                                        />
                                        <VerifikationDialog
                                          onClose={
                                            this.onVerifikationDialogClose
                                          }
                                          onExited={
                                            this.onVerifikationDialogExited
                                          }
                                          open={
                                            this.state.verifikationDialogOpen
                                          }
                                          verifikationId={
                                            this.state.openVerifikationId
                                          }
                                          verifikationBokfdatum={
                                            this.state.openVerifikationBokfdatum
                                          }
                                          tab={this.state.openVerifikationTab}
                                          verifikationNr={
                                            this.state.openVerifikationNr
                                          }
                                          canViewFullVerifikationInfo={
                                            canViewFullVerifikationsRader
                                          }
                                        />
                                      </>
                                    );
                                  }}
                                </EkonomiskAnalysDetailQuery>
                              )}
                            </EDTableCell>
                          </EDTableRow>
                        );
                      }}
                    </EkonomiskAnalysSaveDBSettingMutation>
                  );
                }}
              </EkonomiskAnalysBehorigTillRutinerQuery>
            )}
          </ActiveDatabaseConsumer>
        )}
      </EkonomiskAnalysContextConsumer>
    );
  }
}

export default withStyles(EkonomiskAnalysDetail, styles);
